import { siteConfig } from "@acme/config";
import { createFileRoute } from "@tanstack/react-router";
import { ContactForm } from "~/components/contact-form";
import { NewTab } from "~/components/inside/new-tab";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { PlainList } from "~/components/inside/plain-list";
import { QuietLink } from "~/components/inside/quiet-link";
import { BoardBar } from "~/components/inside/sheets";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { getCtaConfig } from "~/lib/constants/navbar";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getContactPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/contact")({
  component: ContactPage,
  head: () => {
    const seoData = seo({
      description: m.meta_contact_description(),
      keywords:
        "contact Lindevo, AI automation agency Latvia, AI consulting, discovery call, Liepāja, Latvia",
      path: "/contact",
      title: m.meta_contact_title(),
    });
    const structuredData = generateStructuredDataGraph(getContactPageSchemas());
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/**
 * The page that asks for one example.
 *
 * It ends on the ways to reach a person rather than on a closing panel: the
 * whole page is the call to action, so repeating one would ask twice.
 */
function ContactPage() {
  usePageEngagement({ pagePath: "/contact" });

  const cta = getCtaConfig();
  const mailto = `mailto:${siteConfig.contacts.mail}`;

  return (
    <>
      <PageScene variant="split">
        <div>
          <PageLabel>{m.contact_label()}</PageLabel>
          <h1>
            {m.contact_title_1()} <br />
            {m.contact_title_2()}
          </h1>
          <PageIntro>{m.contact_intro()}</PageIntro>

          <div className="actions">
            <a
              className="button button-solid"
              href={cta.href}
              rel="noopener"
              target="_blank"
            >
              {m.inner_cta_call()}
              <NewTab />
            </a>
            <a className="button button-outline" href={mailto}>
              {siteConfig.contacts.mail}
            </a>
          </div>
        </div>

        <div className="app-surface channel-sheet">
          <BoardBar mark title={m.contact_channels_bar()} />
          <dl className="facts">
            <div>
              <dt>{m.contact_channel_email()}</dt>
              <dd>
                <a href={mailto}>{siteConfig.contacts.mail}</a>
              </dd>
            </div>
            <div>
              <dt>{m.contact_channel_phone()}</dt>
              <dd>
                <a href={`tel:${siteConfig.contacts.phone}`}>
                  {siteConfig.contacts.phoneDisplay}
                </a>
              </dd>
            </div>
            <div>
              <dt>{m.contact_channel_location()}</dt>
              <dd>{m.contact_channel_location_value()}</dd>
            </div>
            <div>
              <dt>{m.contact_channel_reply()}</dt>
              <dd>{m.contact_channel_reply_value()}</dd>
            </div>
          </dl>
        </div>
      </PageScene>

      <div className="contact-band wrap">
        <div className="contact-form-sheet">
          <h2>{m.contact_form_title()}</h2>
          <p>{m.contact_form_subtitle()}</p>
          <ContactForm source="contact_page" />
        </div>

        <div className="contact-next">
          <h2>{m.contact_next_title()}</h2>
          <PlainList
            items={[m.contact_next_1(), m.contact_next_2(), m.contact_next_3()]}
          />
          <span className="small-note">{m.contact_next_note()}</span>
        </div>
      </div>

      <section className="contact-elsewhere wrap">
        <h2>{m.contact_elsewhere_title()}</h2>
        <QuietLink to="/faq">{m.contact_elsewhere_faq()}</QuietLink>
        <QuietLink to="/how-we-work">{m.contact_elsewhere_process()}</QuietLink>
      </section>
    </>
  );
}
